<template>
  <div class="stepper-container mx-auto">
    <!-- Stepper Header mit verbesserten visuellen Indikatoren -->
    <div class="stepper-header position-relative">
      <div class="progress position-absolute">
        <div class="progress-bar" role="progressbar" :style="{ width: progress + '%' }"></div>
      </div>
      
      <div class="step-indicators">
        <div 
          v-for="(step, index) in steps" 
          :key="index"
          class="step-indicator"
          :class="{
            'completed': currentStep > index,
            'active': currentStep === index,
            'pending': currentStep < index
          }"
        >
          <div class="step-icon">
            <i v-if="currentStep > index" class="bi bi-check-lg"></i>
            <span v-else>{{ index + 1 }}</span>
          </div>
          <div class="step-label">{{ step.label }}</div>
        </div>
      </div>
    </div>
    <!-- Step Content -->
    <div class="step-content mt-4" ref="stepContent">
      <transition name="fade" mode="out-in">
        <component :is="steps[currentStep].component" :key="currentStep"></component>
      </transition>
    </div>
    <!-- Navigation Buttons -->
    <div class="navigation-buttons d-flex justify-content-between align-items-center">
      <button
        class="btn nav-btn back-btn"
        @click="prevStep"
        :disabled="currentStep === 0 || isLoadingSolutions"
      >
        <i class="bi bi-arrow-left me-2"></i>Back
      </button>
      
      <!-- Clear Data Button - centered in navigation -->
      <button 
        class="clear-data-btn"
        @click="confirmResetProcess"
        title="Reset form and start from beginning"
        :disabled="isLoadingSolutions"
      >
        <i class="bi bi-trash"></i>
        <span class="clear-btn-text">Clear All Data</span>
      </button>
      <div v-if="isLoadingSolutions" class="loading-indicator">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <div class="loading-text">
          <span class="loading-title">Finding solutions...</span>
          <span class="loading-subtitle">This may take 1-2 minutes</span>
          <span class="loading-timer" v-if="loadingElapsedTime > 0">{{ formatElapsedTime() }}</span>
        </div>
      </div>
      <!-- Enhanced Next Step / Find Solutions Button -->
      <div class="next-btn-container">
        <!-- LCA Hint appears when on Solutions step -->
        <div class="lca-hint clickable" v-if="currentStep === steps.length - 1" @click="goToLCAAnalysis">
          <i class="bi bi-arrow-right-circle"></i> proceed to the Environmental Impact Report.
        </div>
        
        <button
          v-if="currentStep < steps.length - 1"
          class="btn nav-btn next-btn"
          @click="nextStep"
          :disabled="isLoadingSolutions"
        >
          <span v-if="currentStep === steps.length - 2">
            <i class="bi bi-search me-2"></i> Find Solutions
          </span>
          <span v-else>
            Next <i class="bi bi-arrow-right ms-2"></i>
          </span>
        </button>
      </div>
    </div>
    
    <!-- Reset Confirmation Modal -->
    <div v-if="showResetConfirmation" class="modal-backdrop" @click="showResetConfirmation = false">
      <div class="reset-modal" @click.stop>
        <div class="modal-header">
          <h5>Clear All Data?</h5>
        </div>
        <div class="modal-body">
          <p>This will delete all entered information and start a new form. Are you sure?</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showResetConfirmation = false">Cancel</button>
          <button class="btn btn-danger" @click="resetProcess">Clear Data</button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import CompanyData from './CompanyData.vue';
import FoodSurplusData from './FoodSurplusData.vue';
import Solutions from './Solutions.vue';
import { useStepperStore } from '@/stores/stepper';
import { useLCAStore } from '@/stores/lca';
import axios from 'axios';
import { useRouter, useRoute } from 'vue-router';
export default {
  name: 'Stepper',
  components: {
    CompanyData,
    FoodSurplusData,
    Solutions,
  },
  setup() {
    const stepperStore = useStepperStore();
    const lcaStore = useLCAStore();
    const router = useRouter();
    const route = useRoute();
    return {
      stepperStore,
      lcaStore,
      router,
      route,
    };
  },
  data() {
    return {
      currentStep: 0,
      steps: [
        { label: 'Company Data', component: 'CompanyData', path: 'company-data' },
        { label: 'Food Surplus', component: 'FoodSurplusData', path: 'food-surplus' },
        { label: 'Solutions Found', component: 'Solutions', path: 'solutions' },
      ],
      primaryColor: '#87B140',
      isLoadingSolutions: false,
      loadingStartTime: null,
      loadingElapsedTime: 0,
      loadingTimer: null,
      showResetConfirmation: false,
    };
  },
  mounted() {
    // Reset loading state on mount to prevent stale state issues
    this.isLoadingSolutions = false;
    this.stopLoadingTimer();
    this.updateCurrentStepFromRoute();
  },
  beforeUnmount() {
    // Clean up timer if component is destroyed while loading
    this.stopLoadingTimer();
  },
  watch: {
    '$route.name': {
      handler: 'updateCurrentStepFromRoute',
      immediate: true,
    },
  },
  computed: {
    progress() {
      return (this.currentStep / (this.steps.length - 1)) * 100;
    },
  },
  methods: {
    updateCurrentStepFromRoute() {
      const currentRouteName = this.$route.name;
      const currentStepIndex = this.steps.findIndex(
        (step) => step.path === currentRouteName
      );
      if (currentStepIndex !== -1) {
        console.log('Route changed - updating step from', this.currentStep, 'to', currentStepIndex);
        console.log('Loading state before reset:', this.isLoadingSolutions);
        
        // Reset loading state when navigating between steps
        // This prevents getting stuck in loading mode when using browser back button
        if (this.isLoadingSolutions) {
          console.log('Resetting loading state due to route change');
          this.isLoadingSolutions = false;
          this.stopLoadingTimer();
        }
        this.currentStep = currentStepIndex;
      }
    },
    async nextStep() {
      // 1) Falls wir uns im CompanyData-Step (Index 0) befinden => geocode ausführen
      if (this.currentStep === 0) {
        // NO loading indicator for geocoding - it's fast
        try {
          // Hier rufen wir die Store-Action auf, die ggf. geocodet
          await this.stepperStore.geocodeCompanyAddress();
        } catch (error) {
          console.error('Error during geocoding:', error);
        }
      }
      // 2) Falls wir uns im FoodSurplus-Step (Index 1) befinden => fetchSolutions ausführen
      //    (hier "Find Solutions" Button)
      if (this.currentStep === 1) {
        // Ensure we're not already loading (prevent double-click issues)
        if (this.isLoadingSolutions) {
          console.warn('Already loading solutions, skipping duplicate request');
          return;
        }
        
        this.isLoadingSolutions = true;
        this.startLoadingTimer();
        let navigationSuccess = false;
        
        try {
          // BUG-FIX: Vor dem Fetch zurücksetzen, um alte Daten zu löschen
          this.stepperStore.resetSolutionData();
          await this.fetchSolutions();
          navigationSuccess = true;
        } catch (error) {
          console.error('Error matching solutions:', error);
          this.stepperStore.setSolutions([]);
          
          // Check if it's a timeout error
          if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            alert('The request is taking longer than expected. Please try again or contact support if the issue persists.');
          } else {
            alert('Error finding solutions. Please try again.');
          }
          navigationSuccess = false;
        } finally {
          // ALWAYS reset loading state, no matter what happens
          this.isLoadingSolutions = false;
          this.stopLoadingTimer();
          console.log('Loading state reset after solutions fetch attempt');
        }
        
        // Only navigate if the fetch was successful
        if (navigationSuccess) {
          // Navigate to solutions page after loading state is properly reset
          if (this.currentStep < this.steps.length - 1) {
            const nextStepPath = this.steps[this.currentStep + 1].path;
            await this.$nextTick(); // Ensure state is fully updated
            this.router.push({ name: nextStepPath });
          }
        }
        return; // Prevent further execution
      }
      // Dann zum nächsten Schritt navigieren (falls vorhanden)
      if (this.currentStep < this.steps.length - 1) {
        const nextStepPath = this.steps[this.currentStep + 1].path;
        this.router.push({ name: nextStepPath });
      }
    },
    prevStep() {
      if (this.currentStep > 0) {
        // BUG-FIX: Wenn wir von Solutions zurückgehen, müssen wir sicherstellen, dass bei erneutem Matching
        // die alten Daten gelöscht werden
        if (this.currentStep === 2) { // Von Solutions zurück zu Food Surplus
          // Scores und Reasoning zurücksetzen, damit sie beim nächsten Matching neu geladen werden
          this.stepperStore.clearSolutionDetails();
        }
        
        const prevStepPath = this.steps[this.currentStep - 1].path;
        this.router.push({ name: prevStepPath });
      }
    },
    
    // Navigate to Environmental Impact Report (new method)
    goToLCAAnalysis() {
      this.router.push({ name: 'LCAStepperView' });
    },
    // fetchSolutions => ruft das Backend (solutions/match) auf
    async fetchSolutions() {
      const userInputs = {
        companyProfile: { ...this.stepperStore.companyProfile },
        wasteInfo: { ...this.stepperStore.wasteInfo },
      };
      
      // Parameter für Score und Reasoning explizit auf "true" setzen
      const includeVCG = true;
      const includeScore = true;
      const includeReasoning = true;
      
      // Erweiterte Query-Parameter für die API-Anfrage
      const queryParams = new URLSearchParams({
        includeVCG: includeVCG,
        includeScore: includeScore, 
        includeReasoning: includeReasoning
      }).toString();
      
      const url = `/solutions/match?${queryParams}`;
      
      // Add timeout to prevent infinite waiting (3 minutes)
      const response = await axios.post(url, { userInputs }, {
        timeout: 180000 // 3 minutes in milliseconds
      });
      const data = response.data;
      
      if (data.solutions) {
        this.stepperStore.setSolutions(data.solutions);
        
        // Details mit Scores und Reasoning speichern
        if (data.details) {
          this.stepperStore.setSolutionDetails(data.details);
        } else {
          this.stepperStore.setSolutionDetails({ scores: {}, reasoning: {} });
        }
      } else {
        this.stepperStore.setSolutions([]);
        this.stepperStore.setSolutionDetails({ scores: {}, reasoning: {} });
      }
    },
    scrollToTop() {
      this.$nextTick(() => {
        const offsetTop =
          this.$el.getBoundingClientRect().top + window.pageYOffset - 100;
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth',
        });
      });
    },
    
    startLoadingTimer() {
      this.loadingStartTime = Date.now();
      this.loadingElapsedTime = 0;
      this.loadingTimer = setInterval(() => {
        this.loadingElapsedTime = Math.floor((Date.now() - this.loadingStartTime) / 1000);
      }, 1000);
    },
    
    stopLoadingTimer() {
      if (this.loadingTimer) {
        clearInterval(this.loadingTimer);
        this.loadingTimer = null;
      }
      this.loadingElapsedTime = 0;
    },
    
    formatElapsedTime() {
      const minutes = Math.floor(this.loadingElapsedTime / 60);
      const seconds = this.loadingElapsedTime % 60;
      if (minutes > 0) {
        return `(${minutes}:${seconds.toString().padStart(2, '0')} elapsed)`;
      }
      return `(${seconds}s elapsed)`;
    },
    
    // Show confirmation dialog before resetting process
    confirmResetProcess() {
      // Check if there's any data to reset
      const hasCompanyData = this.stepperStore.companyProfile.name || 
                             this.stepperStore.companyProfile.location.address;
      const hasWasteData = this.stepperStore.wasteInfo.surplusName || 
                          this.stepperStore.wasteInfo.foodGroups.length > 0;
      const hasSolutions = this.stepperStore.solutions.length > 0;
      
      if (hasCompanyData || hasWasteData || hasSolutions) {
        this.showResetConfirmation = true;
      } else {
        // If no data, just reset without confirmation
        this.resetProcess();
      }
    },
    
    // Reset the entire process and start from beginning
    async resetProcess() {
      this.showResetConfirmation = false;
      
      // Add fade-out animation to content
      const stepContent = this.$refs.stepContent;
      if (stepContent) {
        stepContent.style.opacity = '0.3';
        stepContent.style.transition = 'opacity 0.3s ease';
      }
      
      // Reset all stores
      this.stepperStore.resetStore();
      this.lcaStore.resetStore();
      
      // Reset current step
      this.currentStep = 0;
      
      // Stop any loading timers
      if (this.isLoadingSolutions) {
        this.isLoadingSolutions = false;
        this.stopLoadingTimer();
      }
      
      // Force page refresh to ensure clean state
      await this.$nextTick();
      
      // Navigate to first step and force reload
      await this.router.push({ name: 'company-data' });
      
      // Force reload to ensure all components re-render with fresh data
      window.location.reload();
      
      console.log('Process has been reset');
    },
  },
};
</script>
<style scoped>
.stepper-container {
  max-width: 900px;
  padding: 2.5rem 2rem;
  margin: 1.5rem auto;
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08), 0 5px 15px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(135, 177, 64, 0.1);
  position: relative;
  overflow: hidden;
}
.stepper-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background-color: #87B140; /* Einzelner Grünton statt Gradient */
}
/* Stepper Header Styles */
.stepper-header {
  position: relative;
  margin-bottom: 2.5rem;
  padding: 0 1.5rem;
}

/* Clear Data Button in Navigation */
.clear-data-btn {
  background: transparent;
  border: none;
  color: #dc3545;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  opacity: 0.8;
}

.clear-data-btn::before,
.clear-data-btn::after {
  content: '';
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 24px;
  background: #e0e0e0;
}

.clear-data-btn::before {
  left: -20px;
}

.clear-data-btn::after {
  right: -20px;
}

.clear-data-btn:hover:not(:disabled) {
  background: rgba(220, 53, 69, 0.1);
  color: #a02633;
  opacity: 1;
  border-radius: 6px;
}

.clear-data-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.clear-data-btn i {
  font-size: 0.95rem;
}

.clear-btn-text {
  display: inline;
}
.progress {
  height: 4px;
  width: 100%;
  background-color: #eaeaea;
  border-radius: 4px;
  top: 30px;
  z-index: 1;
  overflow: visible;
}
.progress-bar {
  background-color: #87B140; /* Einzelner Grünton statt Gradient */
  transition: width 0.5s ease;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}
.step-indicators {
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 2;
}
.step-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 33.333%;
  text-align: center;
  transition: transform 0.3s ease;
}
.step-indicator.active {
  transform: translateY(-5px);
}
.step-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  font-weight: 600;
  font-size: 1.25rem;
  border: 2px solid #e0e0e0;
  color: #555;
  transition: all 0.3s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.step-indicator.active .step-icon {
  background-color: #f2f5e7; /* Einfarbiger Hintergrund */
  border-color: #87B140;
  color: #558000;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: scale(1.1);
}
.step-indicator.completed .step-icon {
  background-color: #87B140; /* Einzelner Grünton statt Gradient */
  border-color: #87B140;
  color: #ffffff;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}
.step-indicator.pending .step-icon {
  background-color: #ffffff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}
.step-label {
  color: #555;
  font-weight: 600;
  margin-top: 8px;
  transition: color 0.3s ease;
  font-size: 1rem;
}
.step-indicator.active .step-label {
  color: #333;
  font-weight: 700;
}
.step-indicator.completed .step-label {
  color: #333;
}
/* Step Content */
.step-content {
  min-height: 400px;
  background-color: #ffffff;
  border-radius: 8px;
}
/* Navigation Buttons */
.navigation-buttons {
  margin-top: 2rem;
  padding: 0.5rem 0;
}
/* Added next button container for LCA hint */
.next-btn-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
/* Added LCA hint below next button - made clickable */
.lca-hint {
  color: #558000;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: pulse 2s infinite;
}
.lca-hint.clickable {
  cursor: pointer;
  padding: 4px 12px;
  border-radius: 30px;
  background-color: rgba(135, 177, 64, 0.1);
  transition: all 0.2s ease;
}
.lca-hint.clickable:hover {
  background-color: rgba(135, 177, 64, 0.2);
  transform: translateY(-1px);
}
@keyframes pulse {
  0% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.7;
  }
}
.nav-btn {
  padding: 0.875rem 1.75rem;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
  letter-spacing: 0.02em;
}
.back-btn {
  border: none;
  background-color: #f0f0f0;
  color: #444;
}
.back-btn:hover:not(:disabled) {
  background-color: #e5e5e5;
  color: #222;
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}
.next-btn {
  background-color: #87B140; /* Einzelner Grünton statt Gradient */
  border: none;
  color: white;
}
.next-btn:hover:not(:disabled) {
  background-color: #75a236; /* Dunklerer Ton beim Hover */
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}
.nav-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05) !important;
}
/* Loading Indicator */
.loading-indicator {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #558000;
  background-color: #f2f5e7;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(135, 177, 64, 0.15);
  border: 1px solid rgba(135, 177, 64, 0.2);
  animation: pulse-border 2s infinite;
}

@keyframes pulse-border {
  0%, 100% {
    border-color: rgba(135, 177, 64, 0.2);
  }
  50% {
    border-color: rgba(135, 177, 64, 0.4);
  }
}

.loading-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.loading-title {
  font-weight: 600;
  font-size: 1rem;
}

.loading-subtitle {
  font-weight: 400;
  font-size: 0.875rem;
  color: #6c8a3a;
}

.loading-timer {
  font-weight: 400;
  font-size: 0.75rem;
  color: #7a9a4a;
  font-style: italic;
}

.spinner-border {
  color: #87B140;
  width: 1.5rem;
  height: 1.5rem;
}
/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
/* Responsive Adjustments */
@media (max-width: 768px) {
  .stepper-container {
    padding: 1.5rem 1rem;
    margin: 1rem 0.75rem;
    border-radius: 12px;
  }
  
  .step-icon {
    width: 50px;
    height: 50px;
    font-size: 1rem;
  }
  
  .step-label {
    font-size: 0.9rem;
  }
  
  .navigation-buttons {
    margin-top: 1.5rem;
  }
  
  .nav-btn {
    padding: 0.75rem 1.25rem;
    font-size: 0.95rem;
  }
  
  .next-btn-container {
    align-items: center;
    width: 100%;
  }
  
  .lca-hint {
    text-align: center;
    margin-bottom: 0.75rem;
  }
}
@media (max-width: 576px) {
  .stepper-container {
    padding: 1.25rem 0.75rem;
    margin: 0.75rem 0.5rem;
  }
  
  .stepper-header {
    padding: 0 0.5rem;
    margin-bottom: 2rem;
  }
  
  .step-icon {
    width: 40px;
    height: 40px;
    font-size: 0.9rem;
  }
  
  .step-label {
    font-size: 0.8rem;
  }
  
  .navigation-buttons {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .back-btn, .next-btn {
    width: 100%;
    justify-content: center;
  }
  
  .loading-indicator {
    width: 100%;
    justify-content: center;
    margin: 0.5rem 0;
  }
  
  .loading-text {
    text-align: center;
  }
  
  .lca-hint {
    font-size: 0.85rem;
    width: 100%;
  }
  
  .clear-btn-text {
    display: none;
  }
  
  .clear-data-btn {
    padding: 0.4rem;
    font-size: 1.1rem;
  }
  
  .clear-data-btn::before,
  .clear-data-btn::after {
    display: none;
  }
  
  .clear-data-btn i {
    margin: 0;
  }
}

/* Modal Styles */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(3px);
}

.reset-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  max-width: 450px;
  width: 90%;
  margin: 20px;
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.reset-modal .modal-header {
  padding: 20px 24px 10px;
  border-bottom: 1px solid #e0e0e0;
}

.reset-modal .modal-header h5 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.reset-modal .modal-body {
  padding: 20px 24px;
}

.reset-modal .modal-body p {
  margin: 0;
  color: #555;
  line-height: 1.6;
  font-size: 1rem;
}

.reset-modal .modal-footer {
  padding: 15px 24px 20px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  border-top: 1px solid #e0e0e0;
}

.reset-modal .btn {
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.reset-modal .btn-secondary {
  background: #f8f9fa;
  color: #6c757d;
}

.reset-modal .btn-secondary:hover {
  background: #e9ecef;
  color: #495057;
}

.reset-modal .btn-danger {
  background: #dc3545;
  color: white;
}

.reset-modal .btn-danger:hover {
  background: #c82333;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>